/**
 * Inline "Enable browser notifications?" banner.
 *
 * The browser starts every user at `Notification.permission === 'default'`
 * — i.e. no notifications without an explicit request. The previous
 * push wiring never asked, so nothing ever fired. This component:
 *
 *   1. Renders only if permission is still 'default' and the user
 *      hasn't dismissed the banner in the last 14 days.
 *   2. On click → calls Notification.requestPermission(), then
 *      registers the push subscription (gets the VAPID public key
 *      from the worker, asks the SW's pushManager to subscribe,
 *      POSTs the subscription back).
 *   3. Hides itself once permission is granted (the subscribe call
 *      can run again silently on subsequent page loads via the
 *      effect at the bottom).
 *
 * Failure modes degrade quietly — if any step throws we just keep
 * the banner visible with a one-line error.
 */

import { useEffect, useState } from 'react';
import { api, ApiError } from '../api';

const DISMISS_KEY = 'synfolio.push.dismissed';
const DISMISS_TTL_MS = 14 * 24 * 60 * 60 * 1000;

type State =
  | { kind: 'hidden' }
  | { kind: 'prompt' }
  | { kind: 'busy' }
  | { kind: 'error'; message: string };

export function PushPrompt() {
  const [state, setState] = useState<State>({ kind: 'hidden' });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!('Notification' in window) || !('serviceWorker' in navigator)) return;

    // Permission already decided in some direction — try a silent
    // subscribe (idempotent if we're already subscribed) and never
    // show the banner.
    if (Notification.permission === 'granted') {
      void ensureSubscription().catch(() => undefined);
      return;
    }
    if (Notification.permission === 'denied') return;

    // 'default' — banner-eligible unless the user dismissed recently.
    const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) ?? '0');
    if (Number.isFinite(dismissedAt) && Date.now() - dismissedAt < DISMISS_TTL_MS) {
      return;
    }
    setState({ kind: 'prompt' });
  }, []);

  if (state.kind === 'hidden') return null;

  const onEnable = async () => {
    setState({ kind: 'busy' });
    try {
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') {
        // 'denied' or 'default' (dismissed without choosing) — either
        // way, don't keep asking on every navigation.
        localStorage.setItem(DISMISS_KEY, String(Date.now()));
        setState({ kind: 'hidden' });
        return;
      }
      await ensureSubscription();
      setState({ kind: 'hidden' });
    } catch (e) {
      const msg = e instanceof ApiError
        ? e.message
        : e instanceof Error
          ? e.message
          : 'Failed';
      setState({ kind: 'error', message: msg });
    }
  };

  const onDismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setState({ kind: 'hidden' });
  };

  return (
    <div className="panel-elev mx-3 mt-3 flex flex-wrap items-center gap-3 p-3 text-sm sm:mx-0">
      <span aria-hidden className="text-lg leading-none">🔔</span>
      <div className="min-w-0 flex-1">
        <p className="font-medium text-cream">Want a heads-up when something happens?</p>
        <p className="text-xs text-cream-3">
          Browser notifications for replies, new ideas, and plan updates on
          your boards.
        </p>
        {state.kind === 'error' && (
          <p className="mt-1 text-xs text-rust">Couldn’t set this up: {state.message}</p>
        )}
      </div>
      <button
        type="button"
        className="btn btn-primary text-xs"
        onClick={onEnable}
        disabled={state.kind === 'busy'}
      >
        {state.kind === 'busy' ? 'Enabling…' : 'Enable'}
      </button>
      <button
        type="button"
        className="btn btn-ghost text-xs"
        onClick={onDismiss}
        disabled={state.kind === 'busy'}
      >
        Not now
      </button>
    </div>
  );
}

/**
 * Idempotent: registers (or finds) the service worker, ensures a push
 * subscription exists, and POSTs it to the worker. Calling this
 * repeatedly is safe — pushManager.subscribe() returns the existing
 * subscription if one is already in place.
 */
async function ensureSubscription(): Promise<void> {
  const reg = await getReadyServiceWorker();
  if (!reg) throw new Error('Service worker not available');

  // Use the existing subscription if one is already attached to the
  // SW — saves re-roundtripping the public key.
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    const { key } = await api.pushPublicKey();
    if (!key) throw new Error('Push not configured on server');
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      // Cast: the lib.dom typings narrow BufferSource to a non-shared
      // ArrayBuffer, but the underlying ArrayBufferView we pass is
      // always backed by a regular ArrayBuffer here. Runtime is fine.
      applicationServerKey: vapidKeyFromB64Url(key) as unknown as BufferSource,
    });
  }

  const json = sub.toJSON() as {
    endpoint?: string;
    keys?: { p256dh?: string; auth?: string };
  };
  if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
    throw new Error('Subscription is missing fields');
  }
  await api.pushSubscribe({
    endpoint: json.endpoint,
    keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
  });
}

async function getReadyServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return null;
  // vite-plugin-pwa registers /registerSW.js asynchronously; awaiting
  // .ready gives us whichever SW is in control once it activates.
  try {
    return await navigator.serviceWorker.ready;
  } catch {
    return null;
  }
}

// VAPID public keys arrive base64url-encoded; the Web Push API wants
// raw bytes. Convert without depending on the global Buffer object.
function vapidKeyFromB64Url(s: string): Uint8Array {
  const pad = '='.repeat((4 - (s.length % 4)) % 4);
  const b64 = (s + pad).replace(/-/g, '+').replace(/_/g, '/');
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}
